import React, { useEffect, useState } from 'react';
import { useAgentAuthStore } from '../state/auth';
import { useThemeStore } from '../state/theme';
import Modal from '../components/common/Modal';
import toast from 'react-hot-toast';
import OnboardForm from './OnboardForm';
import { useSearchParams } from 'react-router-dom';
import { JobApplication } from '../types/jobs';
import { ssoLogin } from '../hooks/mutations';
import { LoaderCircle } from 'lucide-react';
interface PastEmployer {
  employerName?: string;
  employerUrl?: string;
  startMonth?: string;
  startYear?: string;
  endMonth?: string;
  endYear?: string;
}

interface Agent extends JobApplication {
  signedProfilePic?: string;
  signedResume?: string;
  signedApplicationVideo?: string;
}

const AgentProfile: React.FC = () => {
  const { agent, isOnboarded, updateAgentAuthState, setIsOnboarded } =
    useAgentAuthStore();
  const { isDarkMode } = useThemeStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const token = searchParams.get('ssoToken');
  const { mutate: loginMutation } = ssoLogin({
    onSuccess: (data) => {
      updateAgentAuthState({
        agent: data.user,
      });
      setIsLoading(false);
    },
    onError: (error) => {
      toast.error('SSO verification failed');
      setIsLoading(false);
    },
  });

  useEffect(() => {
    const verifySsoToken = async () => {
      if (token && !agent) {
        setIsLoading(true);
        loginMutation({ ssoToken: token });
      }
    };
    verifySsoToken();
  }, [token]);

  if (isLoading)
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <LoaderCircle size={36} className="animate-spin" />
      </div>
    );

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  if (!agent) {
    return (
      <div
        className={`min-h-screen min-w-full flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">
            {isOnboarded ? 'Onboarding Complete' : 'Access Restricted'}
          </h1>
          <p>
            {isOnboarded
              ? 'You’re all set! You can now close this tab and relax.'
              : 'To access your profile, please use the secure SSO link provided.'}
          </p>
        </div>
      </div>
    );
  }

  const {
    email,
    personalInfo,
    contactInfo,
    workExperience,
    questionnaire,
    status,
  } = agent;

  const handleLogout = () => {
    updateAgentAuthState({ agent: null });
    window.location.href = 'https://ops.kim.cc/agent/profile';
  };

  const labelClassName = `block text-sm font-medium ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`;

  const inputClassName = `w-full mt-1 p-2 border rounded-md bg-gray-100 ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100'
      : 'bg-gray-100 border-gray-300'
  }`;

  const renderMetadataField = (
    fieldName: string,
    value: string | string[] | undefined
  ) => {
    const displayName = fieldName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    if (Array.isArray(value)) {
      return (
        <div key={fieldName} className="flex flex-col space-y-2">
          <label className={labelClassName}>{displayName}</label>
          <input
            type="text"
            disabled
            value={value.join(', ')}
            className={inputClassName}
          />
        </div>
      );
    }

    return (
      <div key={fieldName} className="flex flex-col space-y-2">
        <label className={labelClassName}>{displayName}</label>
        <input
          type="text"
          disabled
          value={(value as string) || ''}
          className={inputClassName}
        />
      </div>
    );
  };

  const renderPastEmployer = (employers: PastEmployer[]) => {
    return (
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">Past Employers</label>
        <div className="space-y-4">
          {employers.map((employer: PastEmployer, index: number) => (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-md border bg-gray-50"
            >
              <div>
                <label className="text-sm font-medium">Company</label>
                <input
                  type="text"
                  disabled
                  value={employer.employerName || ''}
                  className="w-full mt-1 p-2 border rounded-md bg-gray-100"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Start</label>
                <input
                  type="text"
                  disabled
                  value={`${employer.startMonth || ''} ${employer.startYear || ''}`}
                  className="w-full mt-1 p-2 border rounded-md bg-gray-100"
                />
              </div>
              <div>
                <label className="text-sm font-medium">End</label>
                <input
                  type="text"
                  disabled
                  value={`${employer.endMonth || ''} ${employer.endYear || ''}`}
                  className="w-full mt-1 p-2 border rounded-md bg-gray-100"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getProfilePic = (a: Agent) =>
    a.signedProfilePic ? a.signedProfilePic : a.profilePic;
  const getResume = (a: Agent) => (a.signedResume ? a.signedResume : a.resume);
  const getApplicationVideo = (a: Agent) =>
    a.signedApplicationVideo ? a.signedApplicationVideo : a.applicationVideo;

  return (
    <div
      className={`min-h-screen w-full p-8 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}
    >
      <div className="max-w-4xl mx-auto">
        <div
          className={`rounded-lg shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Agent Profile</h1>
            <button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
            >
              Logout
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClassName}>First Name</label>
              <input
                type="text"
                disabled
                value={personalInfo?.firstName || ''}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Last Name</label>
              <input
                type="text"
                disabled
                value={personalInfo?.lastName || ''}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Highest Education</label>
              <input
                type="text"
                disabled
                value={personalInfo?.highestEducation || ''}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Profile Picture</label>
              {getProfilePic(personalInfo) ? (
                <a
                  href={getProfilePic(personalInfo)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-blue-500 mt-1 p-2 w-fit hover:underline"
                >
                  View Picture
                </a>
              ) : (
                <input
                  type="text"
                  disabled
                  value="No picture available"
                  className={inputClassName}
                />
              )}
            </div>
            <div>
              <label className={labelClassName}>Family Members</label>
              <input
                type="text"
                disabled
                value={personalInfo?.familyMembers || ''}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Dependents</label>
              <input
                type="text"
                disabled
                value={personalInfo?.dependents || ''}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Email</label>
              <input
                type="email"
                disabled
                value={email}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Date of Birth</label>
              <input
                type="text"
                disabled
                value={
                  personalInfo?.dob
                    ? new Date(personalInfo.dob).toLocaleDateString()
                    : ''
                }
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Phone</label>
              <input
                type="text"
                disabled
                value={
                  contactInfo?.wa?.number ? `${contactInfo.wa.number}` : ''
                }
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>City</label>
              <input
                type="text"
                disabled
                value={contactInfo?.address?.city || ''}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Country</label>
              <input
                type="text"
                disabled
                value={contactInfo?.address?.country || ''}
                className={inputClassName}
              />
            </div>

            <div>
              <label className={labelClassName}>Language</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {questionnaire?.languages?.length
                  ? questionnaire?.languages?.map((lang, index) => (
                      <span
                        key={index}
                        className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm"
                      >
                        {lang.language}
                      </span>
                    ))
                  : 'N/A'}
              </div>
            </div>

            <div>
              <label className={labelClassName}>Intro Video</label>
              {getApplicationVideo(questionnaire) ? (
                <a
                  href={getApplicationVideo(questionnaire)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-blue-500 mt-1 p-2 w-fit hover:underline"
                >
                  Watch Video
                </a>
              ) : (
                <input
                  type="text"
                  disabled
                  value="No video available"
                  className={inputClassName}
                />
              )}
            </div>
            <div>
              <label className={labelClassName}>Resume</label>
              {getResume(workExperience) ? (
                <a
                  href={getResume(workExperience)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-blue-500 mt-1 p-2 w-fit hover:underline"
                >
                  View Resume
                </a>
              ) : (
                <input
                  type="text"
                  disabled
                  value="No resume available"
                  className={inputClassName}
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
            <div>
              <label className={labelClassName}>Status</label>
              <input
                type="text"
                disabled
                value={status || ''}
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Begin Work</label>
              <input
                type="text"
                disabled
                value={workExperience?.beginWork || ''}
                className={inputClassName}
              />
            </div>
          </div>

          {/* Past Employers */}
          {workExperience?.pastEmployers &&
            workExperience.pastEmployers.length > 0 &&
            renderPastEmployer(workExperience.pastEmployers)}

          {/* Dynamic Metadata Fields */}
          {agent?.metadata && Object.keys(agent.metadata).length > 0 && (
            <div className="flex flex-col gap-4 mt-4">
              {Object.entries(agent.metadata).map(([fieldName, value]) =>
                renderMetadataField(fieldName, value)
              )}
            </div>
          )}
        </div>

        {!isOnboarded && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md"
            >
              Onboarding Form
            </button>
          </div>
        )}
        <Modal
          isOpen={isModalOpen}
          onRequestClose={onModalClose}
          title="Agent Onboarding"
          contentClassName="p-4"
          width="40%"
        >
          <OnboardForm
            onSuccess={() => {
              toast.success('Onboarding successfull');
              setIsOnboarded(true);
              onModalClose();
            }}
            onError={(error) => {
              toast.error(error.message);
              onModalClose();
            }}
            agent={agent}
          />
        </Modal>
      </div>
    </div>
  );
};

export default AgentProfile;
